import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Form, Row, Tooltip } from 'antd';

import { Button } from '@nvision/ui';

import FormInputNumber from '@/views/components/Inputs/FormInputNumber';
import { useFocusableRef } from '@/views/screens/Claims/common/hooks/useFocusableRef';

import { ReactComponent as ProductAddIcon } from '../../../../../../assets/icons/product-add.svg';
import { FLOAT_NUMBER_VALIDATION_REGEX, INTEGER_NUMBER_VALIDATION_REGEX } from '../../../../../../contants/ClaimsEnums';
import { getStyleVariableValue } from '../../../../../../utilz';
import { routeFileSelector } from '../../../../../../utilz/i18n';
import { LDClaimsItemCalculation } from '../../../../../../utilz/PreClaimsUtils';
import IconButton from '../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import FormTextArea from '../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../components/Inputs/HiddenFormInput';
import Paper from '../../../../../components/Paper/Paper';
import ProductSelector from '../../../../../components/Selectors/AsynchSearch/ProductSelector';
import ProductUnitUomSelector from '../../../../../components/Selectors/ProductUnitUomSelector';
import SaveCancelButtons from '../../../../Shipment/components/SaveCancelButtons';
import { useGetLADCustomerSettings, useGetSelectedCompanyId } from '../../../hooks';
import { ClaimsMitigatedTypeSelect, ClaimsPercentageValueSelect, RegularReleaseSelect } from '../../../PreClaims';

const LADClaimsItemCard = ({
  handleAddNew,
  editMode = false,
  isLastItem = false,
  shadowVisible = true,
  index = 0,
  itemDetails,
  setEditMode,
  handleCancel,
  handleSubmit,
  handleDelete,
  handleNewProductModal,
  shouldUpdateClaimantValueFromItemModal = false,
  totalItemsCount = 1,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm();
  const selectedCompanyId = useGetSelectedCompanyId();
  const { data: customerSettings } = useGetLADCustomerSettings(selectedCompanyId);

  const handleCalculations = () => {
    setTimeout(() => {
      const data = LDClaimsItemCalculation(form.getFieldsValue(), customerSettings);
      form.setFieldsValue(data);
    }, 100);
  };

  useEffect(() => {
    if (itemDetails) {
      form.setFieldsValue(itemDetails);
    }
  }, [itemDetails, form]);

  const cardRef = useFocusableRef('#itemCode-origin', { isEnabled: itemDetails?.isNew && !itemDetails?.isInitial });

  const handleProductCodeChange = (value) => {
    if (!value['1']) {
      form.setFieldsValue({
        fullRelFlag: value.fullRelFlag,
        itemDesc: value?.productDesc,
        itemUnitMeasure: value.productUnitMeasure,
        itemUnitWeight: value.productWeight,
        ...(shouldUpdateClaimantValueFromItemModal ? { claimantValue: value.claimantValue } : {}),
      });
    }
  };

  return (
    <div ref={cardRef}>
      <Paper className={`border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`}>
        <Form onFinish={(data) => handleSubmit(data, index)} initialValues={itemDetails} form={form}>
          <div className="border-bottom-muted pt-8 pb-10 px-16">
            <Row justify="space-between align-items-center">
              <Col sm={6}>
                <Row align="middle" gutter={15}>
                  <Col className="flex-1">
                    <Row align="middle" gutter={15}>
                      <Col className="flex-1">
                        <ProductSelector
                          name="itemCode"
                          label="Item Code"
                          placeholder="Select Item Code"
                          singleLineMode
                          margin="m-0"
                          editMode={editMode}
                          onChange={handleProductCodeChange}
                          colon
                          form={form}
                          rules={[
                            {
                              message: t('translation:default.fields.productDesc.message'),
                              required: true,
                            },
                          ]}
                        />
                      </Col>
                      {editMode && (
                        <Col>
                          <IconButton onClick={() => handleNewProductModal(index)}>
                            <Tooltip
                              color={getStyleVariableValue('--label')}
                              title={t('translation:shipmentAdd.productDetails.createNewProduct')}
                              placement="bottomLeft"
                            >
                              <SvgConverter Icon={ProductAddIcon} color="header" />
                            </Tooltip>
                          </IconButton>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
          <div className="px-15 pt-10">
            <Row gutter={15}>
              <Col sm={6}>
                <FormTextArea
                  label="Item Description"
                  placeholder="Enter Item Description"
                  editMode={editMode}
                  {...(editMode ? { rows: 3 } : {})}
                  name="itemDesc"
                  margin="mb-10"
                  maxLength={25}
                  enableAutoFocus={false}
                  rules={[
                    {
                      message: 'Please enter Item Description',
                      required: true,
                    },
                  ]}
                />
                <FormInputNumber
                  label="Quantity"
                  placeholder="Enter Quantity"
                  editMode={editMode}
                  name="itemQuantity"
                  margin="mb-10"
                  inputType="number"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      message: 'Please enter item Quantity',
                      required: true,
                    },
                    {
                      message: "Item quantity can't be a negative or zero value",
                      pattern: INTEGER_NUMBER_VALIDATION_REGEX,
                    },
                  ]}
                  onChange={handleCalculations}
                  precision={2}
                />
                <FormInputNumber
                  label="Item Unit Weight"
                  placeholder="Enter Item Unit Weight"
                  editMode={editMode}
                  name="itemUnitWeight"
                  margin="mb-10"
                  inputType="number"
                  precision={2}
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      message: 'Please enter Item Unit Weight',
                      required: true,
                    },
                    {
                      message: "Item Unit Weight can't be a negative or zero value",
                      pattern: FLOAT_NUMBER_VALIDATION_REGEX,
                    },
                  ]}
                  onChange={handleCalculations}
                />
                <FormInputNumber
                  label="Total Weight"
                  placeholder="Enter Total Weight"
                  editMode={editMode}
                  name="totalWeight"
                  margin="mb-15"
                  precision={2}
                  inputType="number"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      message: "Total Weight can't be a negative or zero value",
                      pattern: FLOAT_NUMBER_VALIDATION_REGEX,
                    },
                  ]}
                />
              </Col>
              <Col sm={6}>
                <ProductUnitUomSelector
                  label="Unit of Measure"
                  placeholder="Enter Unit of Measure"
                  editMode={editMode}
                  name="itemUnitMeasure"
                  margin="mb-10"
                  rules={[
                    {
                      message: 'Please select Unit of Measure',
                      required: true,
                    },
                  ]}
                  shouldUseNativeSelect
                />
                <RegularReleaseSelect
                  label="Full/Release Value"
                  placeholder="Enter Full/Release Value"
                  editMode={editMode}
                  name="fullRelFlag"
                  margin="mb-10"
                  rules={[
                    {
                      message: 'Please select Full/Release Value',
                      required: true,
                    },
                  ]}
                  onChange={handleCalculations}
                />
                <FormInputNumber
                  label="Claimant Value"
                  placeholder="Enter Claimant Value"
                  editMode={editMode}
                  name="claimantValue"
                  margin="mb-10"
                  inputType="number"
                  precision={2}
                  allowNegative
                />
              </Col>
              <Col sm={6}>
                <FormInputNumber
                  label="Filed Value"
                  placeholder="Enter Filed Value"
                  editMode={editMode}
                  name="value"
                  margin="mb-18"
                  inputType="number"
                  onChange={handleCalculations}
                  precision={2}
                  allowNegative
                />
                <FormInputNumber
                  label="Total Value"
                  placeholder="Enter Total Value"
                  editMode={editMode}
                  name="totalValue"
                  margin="mb-18"
                  inputType="number"
                  precision={2}
                  allowNegative
                />
                <ClaimsPercentageValueSelect
                  label="Percentage/Value"
                  placeholder="Select Percentage/Value"
                  editMode={editMode}
                  name="smType"
                  margin="mb-10"
                  onChange={handleCalculations}
                />
              </Col>
              <Col sm={6}>
                <ClaimsMitigatedTypeSelect
                  label="Mitigated Type"
                  placeholder="Select Mitigated Type"
                  editMode={editMode}
                  name="smFlag"
                  margin="mb-10"
                  onChange={handleCalculations}
                />
                <FormInputNumber
                  label="Mitigated Amount"
                  placeholder="Enter Mitigated Amount"
                  editMode={editMode}
                  name="smAmount"
                  margin="mb-10"
                  inputType="number"
                  onChange={handleCalculations}
                  precision={2}
                  allowNegative
                />
                <FormInputNumber
                  label="Product Claim Amount"
                  placeholder="Enter Product Claim Amount"
                  editMode={false}
                  name="productClaimAmount"
                  margin="mb-10"
                  inputType="number"
                  precision={2}
                  allowNegative
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-end pb-10">
              <SaveCancelButtons
                editMode={editMode}
                isModified
                lightMode={false}
                setEditMode={(val) => setEditMode(index, val)}
                handleRemove={() => handleDelete(index)}
                isNew={itemDetails.isNew}
                idSuffix={`product-details-${index + 1}`}
                onCancel={() => handleCancel(index, itemDetails.isNew)}
                hideDeleteButton={totalItemsCount <= 1}
              />
              <Row className="align-items-center px-15" justify="space-between">
                <Col>
                  <Button onClick={() => handleAddNew(index)} lightMode={false} size="md">
                    Add New Item
                  </Button>
                </Col>
              </Row>
            </div>
          </div>
          <HiddenFormInput name="itemSeq" />
          <HiddenFormInput name="oldCommentType" />
          <HiddenFormInput name="isNew" />
          <HiddenFormInput name="editMode" />
          <HiddenFormInput name="claimKey" />
          <HiddenFormInput name="dirtyFlag" />
        </Form>
      </Paper>
    </div>
  );
};

export default LADClaimsItemCard;
